import { useRef, useState, useEffect, useMemo } from 'react';
import { categoryColor, type PipelineNode, type PipelineEdge, type GroupNode, type StickyNote, type GroupType, type SkillDef, type Scheduler } from '@/mocks/pipeline';
import { SCHEDULER_W } from '@/pages/home/canvasLayout';
import { NODE_W, NODE_H, nodeInGroup } from '@/pages/home/flowUtils';
import { PROMPT_GAP, PROMPT_NODE_ID, PROMPT_W, PROMPT_X, PROMPT_Y, isPromptSelected } from '@/pages/home/promptNode';
import PromptNode from './PromptNode';

const statusColor: Record<PipelineNode['status'], string> = {
  idle: 'var(--texts)',
  running: 'var(--accent)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  error: 'var(--error)',
};

const GROUP_STYLE: Record<GroupType, { color: string; icon: string }> = {
  process: { color: 'var(--accent)', icon: 'ri-git-merge-line' },
  loop: { color: 'var(--success)', icon: 'ri-loop-left-line' },
};

const groupBgClass = (type: GroupType) => {
  if (type === 'process') return 'bg-accentSoft';
  return 'bg-success/10';
};

const groupBorderClass = (type: GroupType) => {
  if (type === 'process') return 'border-accent';
  return 'border-success';
};

const nodeInputOutputBgClass = () => {
  return 'bg-bg';
};

const stickyBgClass = () => {
  return 'bg-warning';
};

type Props = {
  skills: SkillDef[];
  nodes: PipelineNode[];
  edges: PipelineEdge[];
  groups: GroupNode[];
  stickies: StickyNote[];
  schedulers: Scheduler[];
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  onNodeMove: (id: string, x: number, y: number) => void;
  onDropSkill: (skillId: string, x: number, y: number) => void;
  onDropGroup: (type: GroupType, x: number, y: number) => void;
  onDropSticky: (x: number, y: number) => void;
  onDropScheduler: (x: number, y: number) => void;
  onMoveGroup: (id: string, x: number, y: number) => void;
  onMoveSticky: (id: string, x: number, y: number) => void;
  onUpdateGroupTitle: (id: string, title: string) => void;
  onUpdateGroupIterations: (id: string, iterations: number) => void;
  onRemoveGroup: (id: string) => void;
  onUpdateSticky: (id: string, text: string) => void;
  onRemoveSticky: (id: string) => void;
  onMoveScheduler: (id: string, x: number, y: number) => void;
  onUpdateScheduler: (id: string, patch: Partial<Scheduler>) => void;
  onRemoveScheduler: (id: string) => void;
  onEditNode: (id: string) => void;
  onViewSkill: (skillId: string | null) => void;
  onRemoveNode: (id: string) => void;
  previewSkillId: string | null;
  running: boolean;
  locked?: boolean;
  activeEdgeIds: Set<string>;
  prompt: string;
  viewingPrompt: boolean;
  onViewPrompt: () => void;
};

export default function Canvas({
  skills, nodes, edges, groups, stickies, schedulers, selectedId, setSelectedId,
  onNodeMove, onDropSkill, onDropGroup, onDropSticky, onDropScheduler,
  onMoveGroup, onMoveSticky, onUpdateGroupTitle, onUpdateGroupIterations, onRemoveGroup,
  onUpdateSticky, onRemoveSticky, onMoveScheduler, onUpdateScheduler, onRemoveScheduler, onEditNode, onViewSkill, onRemoveNode, previewSkillId, running, locked, activeEdgeIds,
  prompt, viewingPrompt, onViewPrompt,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragNode, setDragNode] = useState<{ id: string; ox: number; oy: number } | null>(null);
  const [dragGroup, setDragGroup] = useState<{ id: string; ox: number; oy: number } | null>(null);
  const [dragSticky, setDragSticky] = useState<{ id: string; ox: number; oy: number } | null>(null);
  const [dragScheduler, setDragScheduler] = useState<{ id: string; ox: number; oy: number } | null>(null);
  const [panDrag, setPanDrag] = useState<{ x: number; y: number } | null>(null);

  const skillById = useMemo(() => Object.fromEntries(skills.map((s) => [s.id, s])), [skills]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (dragNode || dragGroup || dragSticky || dragScheduler) {
        const rect = wrapRef.current!.getBoundingClientRect();
        const x = (e.clientX - rect.left - pan.x) / zoom;
        const y = (e.clientY - rect.top - pan.y) / zoom;
        if (dragNode) onNodeMove(dragNode.id, x - dragNode.ox, y - dragNode.oy);
        else if (dragGroup) onMoveGroup(dragGroup.id, x - dragGroup.ox, y - dragGroup.oy);
        else if (dragSticky) onMoveSticky(dragSticky.id, x - dragSticky.ox, y - dragSticky.oy);
        else if (dragScheduler) onMoveScheduler(dragScheduler.id, x - dragScheduler.ox, y - dragScheduler.oy);
      } else if (panDrag) {
        setPan({ x: e.clientX - panDrag.x, y: e.clientY - panDrag.y });
      }
    };
    const onUp = () => { setDragNode(null); setDragGroup(null); setDragSticky(null); setDragScheduler(null); setPanDrag(null); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragNode, dragGroup, dragSticky, dragScheduler, panDrag, pan, zoom, onNodeMove, onMoveGroup, onMoveSticky, onMoveScheduler]);

  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) e.preventDefault();
    const delta = -e.deltaY * 0.001;
    setZoom((z) => Math.min(2, Math.max(0.25, z + delta)));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (locked) return;
    const rect = wrapRef.current!.getBoundingClientRect();
    const px = (e.clientX - rect.left - pan.x) / zoom;
    const py = (e.clientY - rect.top - pan.y) / zoom;
    const snap = (v: number) => Math.round(v / 20) * 20;

    const skillId = e.dataTransfer.getData('skill/id');
    const toolType = e.dataTransfer.getData('tool/type');

    if (skillId) {
      onDropSkill(skillId, snap(px - NODE_W / 2), snap(py - NODE_H / 2));
    } else if (toolType === 'sticky') {
      onDropSticky(snap(px - 100), snap(py - 20));
    } else if (toolType === 'scheduler') {
      onDropScheduler(snap(px - 110), snap(py - 20));
    } else if (toolType === 'process' || toolType === 'loop') {
      onDropGroup(toolType, snap(px - 170), snap(py - 30));
    }
  };

  const edgePath = (from: PipelineNode, to: PipelineNode) => {
    const x1 = from.x + NODE_W;
    const y1 = from.y + NODE_H / 2;
    const x2 = to.x;
    const y2 = to.y + NODE_H / 2;
    const mx = (x1 + x2) / 2;
    return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
  };

  const fitToView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const localFromMouse = (e: React.MouseEvent) => {
    const rect = wrapRef.current!.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom,
    };
  };

  return (
    <div
      ref={wrapRef}
      className="flex-1 relative overflow-hidden dot-grid select-none"
      onWheel={handleWheel}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      onMouseDown={(e) => {
        if (e.button === 1 || (e.button === 0 && e.target === wrapRef.current)) {
          setPanDrag({ x: e.clientX - pan.x, y: e.clientY - pan.y });
          if (e.target === wrapRef.current) setSelectedId(null);
        }
      }}
    >
      <div
        className="absolute top-0 left-0"
        style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}
      >
        <PromptNode
          prompt={prompt}
          selected={isPromptSelected(selectedId)}
          viewing={viewingPrompt}
          onSelect={() => setSelectedId(PROMPT_NODE_ID)}
          onView={onViewPrompt}
        />
        {nodes.length === 0 && groups.length === 0 && stickies.length === 0 && (
          <div
            className="absolute text-left pointer-events-none"
            style={{ left: PROMPT_X + PROMPT_W + PROMPT_GAP / 2, top: PROMPT_Y + 40, width: 240 }}
          >
            <div className="text-textp text-sm font-medium">Drag skills to the right</div>
            <div className="text-texts text-xs mt-1">The prompt is the outcome this pipeline achieves</div>
          </div>
        )}
        {/* groups (behind everything) */}
        {groups.map((g) => {
          const style = GROUP_STYLE[g.type];
          const members = nodes.filter((n) => nodeInGroup(n, g));
          const isRunning = g.status === 'running';
          return (
            <div
              key={g.id}
              className={`absolute rounded-xl border-2 border-dashed ${isRunning ? 'pulse-node' : ''} ${groupBgClass(g.type)} ${groupBorderClass(g.type)}`}
              style={{ left: g.x, top: g.y, width: g.w, height: g.h }}
            >
              <div
                className="flex items-center justify-between px-2.5 py-1.5 cursor-move"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const p = localFromMouse(e);
                  setDragGroup({ id: g.id, ox: p.x - g.x, oy: p.y - g.y });
                }}
              >
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <i className={`${style.icon} text-sm shrink-0`} style={{ color: style.color }} />
                  <input
                    value={g.title}
                    onChange={(e) => onUpdateGroupTitle(g.id, e.target.value)}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="bg-transparent text-[12px] font-semibold text-textp outline-none border-b border-transparent focus:border-accent min-w-0 flex-1"
                  />
                </div>
                <div className="flex items-center gap-1.5 shrink-0" onMouseDown={(e) => e.stopPropagation()}>
                  {g.type === 'loop' && (
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-texts">×</span>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={g.iterations}
                        onChange={(e) => onUpdateGroupIterations(g.id, Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-9 bg-surface border border-border rounded text-[11px] text-textp text-center outline-none focus:border-accent"
                      />
                    </div>
                  )}
                  <button onClick={() => onRemoveGroup(g.id)} className="text-texts hover:text-error" title="Remove">
                    <i className="ri-close-line text-sm" />
                  </button>
                </div>
              </div>
              {members.length === 0 && (
                <div className="px-3 text-[10px] text-texts pointer-events-none">
                  {g.type === 'loop' ? 'Drag a single skill inside to repeat it' : 'Drag skills inside to group them'}
                </div>
              )}
            </div>
          );
        })}

        {/* edges */}
        <svg className="absolute overflow-visible" style={{ width: 1, height: 1, left: 0, top: 0 }}>
          <defs>
            <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--border)" />
            </marker>
            <marker id="arrow-active" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--accent)" />
            </marker>
          </defs>
          {edges.map((e) => {
            const from = nodes.find((n) => n.id === e.from);
            const to = nodes.find((n) => n.id === e.to);
            if (!from || !to) return null;
            const active = activeEdgeIds.has(e.id);
            return (
              <g key={e.id}>
                <path
                  d={edgePath(from, to)}
                  fill="none"
                  stroke={active ? 'var(--accent)' : 'var(--border)'}
                  strokeWidth={active ? 2.5 : 1.8}
                  markerEnd={`url(#${active ? 'arrow-active' : 'arrow'})`}
                  className={active ? 'edge-flow' : ''}
                />
              </g>
            );
          })}
        </svg>

        {/* skill nodes */}
        {nodes.map((n) => {
          const skill = skillById[n.skillId];
          const name = skill?.name ?? n.skillId;
          const icon = skill?.icon || 'ri-cpu-line';
          const description = skill?.description ?? '';
          const selected = n.id === selectedId;
          const viewing = n.skillId === previewSkillId;
          const color = categoryColor(skill?.category || 'Other');
          return (
            <div
              key={n.id}
              onMouseDown={(e) => {
                e.stopPropagation();
                if (locked) {
                  setSelectedId(n.id);
                  return;
                }
                const p = localFromMouse(e);
                setDragNode({ id: n.id, ox: p.x - n.x, oy: p.y - n.y });
                setSelectedId(n.id);
              }}
              className={`absolute rounded-2xl bg-surface border overflow-hidden transition-colors ${selected ? 'border-accent' : 'border-border'} ${running && n.status === 'running' ? 'pulse-node' : ''}`}
              style={{ left: n.x, top: n.y, width: NODE_W, minHeight: NODE_H, borderColor: selected ? 'var(--accent)' : undefined }}
            >
              <div className="flex items-center justify-between px-3 pt-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: `${color}22`, color }}>
                    <i className={`${icon} text-sm`} />
                  </div>
                  <div className="text-[13px] font-semibold text-textp truncate">{name}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {n.note && <i className="ri-sticky-note-line text-texts text-xs" title={n.note} />}
                  <button
                    onClick={() => onEditNode(n.id)}
                    onMouseDown={(e) => e.stopPropagation()}
                    title="Edit content"
                    className="text-texts hover:text-accent"
                  >
                    <i className="ri-edit-line text-xs" />
                  </button>
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: statusColor[n.status], boxShadow: n.status !== 'idle' ? `0 0 8px ${statusColor[n.status]}` : undefined }}
                  />
                </div>
              </div>
              <div className="px-3 pb-2.5 pt-1">
                <div className="text-[11px] text-texts leading-snug">{description}</div>
                <button
                  type="button"
                  aria-pressed={viewing}
                  onClick={() => onViewSkill(viewing ? null : n.skillId)}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md border ${viewing ? 'text-accent bg-accent/15 border-accent/40' : 'text-texts border-border hover:text-textp hover:border-accent/40'}`}
                >
                  <i className={`${viewing ? 'ri-eye-off-line' : 'ri-eye-line'} text-xs`} />
                  {viewing ? 'Hide' : 'View'}
                </button>
                  <button
                    onClick={() => onRemoveNode(n.id)}
                    onMouseDown={(e) => e.stopPropagation()}
                    disabled={locked}
                    className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md border border-border text-texts hover:text-error hover:border-error/40 disabled:opacity-40"
                    title="Remove node"
                  >
                  <i className="ri-delete-bin-line text-xs" />
                  Remove
                </button>
              </div>
              
              {/* input dot */}
              <div className={`absolute -left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${nodeInputOutputBgClass()} border-2 border-border hover:border-accent`} />
              {/* output dot */}
              <div className={`absolute -right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${nodeInputOutputBgClass()} border-2 border-border hover:border-accent`} />
            </div>
          );
        })}

        {(schedulers || []).map((s) => (
          <div
            key={s.id}
            className={`absolute flex flex-col rounded-xl overflow-hidden bg-surface border ${selectedId === s.id ? 'border-accent' : 'border-border'}`}
            style={{ left: s.x, top: s.y, width: SCHEDULER_W }}
            data-testid="canvas-scheduler"
          >
            <div
              className="flex items-center justify-between px-2 py-1.5 bg-accentSoft cursor-move"
              onMouseDown={(e) => {
                e.stopPropagation();
                const p = localFromMouse(e);
                setDragScheduler({ id: s.id, ox: p.x - s.x, oy: p.y - s.y });
                setSelectedId(s.id);
              }}
            >
              <span className="text-[10px] font-semibold text-accent uppercase tracking-wide">
                <i className="ri-time-line mr-1" />Scheduler
              </span>
              <button onClick={() => onRemoveScheduler(s.id)} onMouseDown={(e) => e.stopPropagation()} className="text-texts hover:text-error" title="Remove">
                <i className="ri-close-line text-sm" />
              </button>
            </div>
            <div className="p-2 space-y-1.5">
              <input
                value={s.title || ''}
                onChange={(e) => onUpdateScheduler(s.id, { title: e.target.value })}
                placeholder="Listen"
                className="w-full bg-bg border border-border rounded px-1.5 py-1 text-[12px] text-textp outline-none"
              />
              <input
                value={s.cadence || ''}
                onChange={(e) => onUpdateScheduler(s.id, { cadence: e.target.value })}
                placeholder="@every 6h"
                className="w-full bg-bg border border-border rounded px-1.5 py-1 text-[12px] text-textp outline-none font-mono"
              />
              <label className="flex items-center gap-1.5 text-[11px] text-texts">
                <input
                  type="checkbox"
                  checked={s.enabled}
                  onChange={(e) => onUpdateScheduler(s.id, { enabled: e.target.checked })}
                />
                Enabled
              </label>
            </div>
          </div>
        ))}

        {/* sticky notes (on top) */}
        {stickies.map((s) => (
          <div
            key={s.id}
            className="absolute flex flex-col rounded-lg overflow-hidden bg-warning border border-black/10"
            style={{ left: s.x, top: s.y, width: s.w, height: s.h }}
          >
            <div
              className="flex items-center justify-between px-2 py-1 bg-black/10 cursor-move shrink-0"
              onMouseDown={(e) => {
                e.stopPropagation();
                const p = localFromMouse(e);
                setDragSticky({ id: s.id, ox: p.x - s.x, oy: p.y - s.y });
              }}
            >
              <span className="text-[10px] font-semibold text-black uppercase tracking-wide">Note</span>
              <button onClick={() => onRemoveSticky(s.id)} onMouseDown={(e) => e.stopPropagation()} className="text-black/70 hover:text-black" title="Remove">
                <i className="ri-close-line text-sm" />
              </button>
            </div>
            <textarea
              value={s.text}
              onChange={(e) => onUpdateSticky(s.id, e.target.value)}
              placeholder="Write a note..."
              className="flex-1 w-full bg-transparent text-black text-[12px] leading-snug p-2 resize-none outline-none placeholder:text-black/60"
            />
          </div>
        ))}
      </div>

      {/* controls */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-1.5 bg-surface/80 backdrop-blur border border-border rounded-lg p-1">
        <button onClick={() => setZoom((z) => Math.min(2, z + 0.1))} className="w-7 h-7 flex items-center justify-center text-texts hover:text-textp rounded-md hover:bg-surface2" title="Zoom in">
          <i className="ri-add-line" />
        </button>
        <button onClick={() => setZoom((z) => Math.max(0.25, z - 0.1))} className="w-7 h-7 flex items-center justify-center text-texts hover:text-textp rounded-md hover:bg-surface2" title="Zoom out">
          <i className="ri-subtract-line" />
        </button>
        <button onClick={fitToView} className="w-7 h-7 flex items-center justify-center text-texts hover:text-textp rounded-md hover:bg-surface2" title="Fit to view">
          <i className="ri-focus-3-line" />
        </button>
      </div>
      <div className="absolute bottom-4 left-4 text-[11px] font-mono text-texts bg-surface/80 backdrop-blur border border-border rounded-md px-2 py-1">
        {Math.round(zoom * 100)}%
      </div>
      {locked && (
        <div className="absolute inset-0 z-10 bg-bg/30 cursor-not-allowed flex items-start justify-center pt-3 pointer-events-auto">
          <div className="text-xs text-texts bg-surface border border-border rounded-md px-3 py-1.5">Canvas locked while running</div>
        </div>
      )}
    </div>
  );
}